import { useEffect, useState } from 'react'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import { Modal, Button, ButtonVariant, Body, Caption } from '@meiuca/ui-components'
import { formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { AlertCircle, Calendar, Clock, Loader2, MapPin } from 'lucide-react'
import { formatTimeRange } from '@/lib/trainingCenterLabels'
import { toastManager } from '@/lib/toastManager'
import { ClassPackagePurchaseFlow } from '@/components/ClassPackagePurchaseFlow'
import {
  makeupService,
  type MakeupCheckoutBlockReason,
  type MakeupCheckoutOptions
} from '@/services/makeupService'
import type { WorkoutListItem } from '@/services/workoutService'

/**
 * Wire values are the backend enum's Swift case names (camelCase). Falls back
 * to a generic message so an unexpected reason never renders blank.
 */
const BLOCK_REASON_MESSAGES: Record<MakeupCheckoutBlockReason, string> = {
  occurrenceNotFound: 'Esta aula não está mais disponível.',
  occurrenceNotJoinable: 'Esta aula não está mais aberta para inscrição.',
  selfEnrollmentNotAllowed: 'Esta aula não aceita inscrição pelo app.',
  levelNotAllowed: 'Esta aula é para outro nível.',
  occurrenceFull: 'A aula já está cheia.',
  participantAlreadyExists: 'Você já está inscrito nesta aula.',
  customerNotFound: 'Você não é aluno deste centro de treinamento.',
  noMakeUpCredit: 'Você não tem reposições disponíveis.',
  noUsableMakeUpCredit: 'Suas reposições ainda não estão liberadas para uso.',
  noMakeUpCreditForModality: 'Suas reposições são de outra modalidade e não valem para esta aula.'
}

/** Error codes of `POST .../self-enroll` (snake_case `MeiucaHttpError.code`). */
const ENROLL_ERROR_MESSAGES: Record<string, string> = {
  training_center_class_occurrence_not_found: 'Esta aula não está mais disponível.',
  training_center_class_occurrence_not_joinable: 'Esta aula não está mais aberta para inscrição.',
  self_enrollment_not_allowed: 'Esta aula não aceita inscrição pelo app.',
  training_center_customer_not_found: 'Você não é aluno deste centro de treinamento.',
  level_not_allowed: 'Esta aula é para outro nível.',
  training_center_class_occurrence_full: 'A aula já está cheia.',
  participant_already_exists: 'Você já está inscrito nesta aula.',
  no_makeup_credit_available: 'Você não tem reposições disponíveis.'
}

type CheckoutState =
  | { kind: 'loading' }
  | { kind: 'ready'; options: MakeupCheckoutOptions }
  | { kind: 'blocked'; reason: MakeupCheckoutBlockReason | null; options?: MakeupCheckoutOptions }
  | { kind: 'error' }

/** Lack-of-credit blocks are solvable by buying a package when the CT sells them. */
const NO_CREDIT_REASONS: MakeupCheckoutBlockReason[] = [
  'noMakeUpCredit',
  'noUsableMakeUpCredit',
  'noMakeUpCreditForModality'
]

interface MakeupCheckoutModalProps {
  open: boolean
  occurrence: WorkoutListItem | null
  onClose: () => void
  /** Called after a successful enrollment so the agenda can refresh. */
  onEnrolled: () => void
}

export function MakeupCheckoutModal({ open, occurrence, onClose, onEnrolled }: MakeupCheckoutModalProps) {
  const [state, setState] = useState<CheckoutState>({ kind: 'loading' })
  const [isEnrolling, setIsEnrolling] = useState(false)
  // Bumped when the package checkout completes: the credits just arrived, so
  // the class checkout reloads its options and lands on the makeup mode.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!open || !occurrence) return
    let cancelled = false

    const loadOptions = async () => {
      setState({ kind: 'loading' })
      try {
        const options = await makeupService.getCheckoutOptions(
          occurrence.trainingCenterId,
          occurrence.id
        )
        if (cancelled) return
        if (options.mode === 'blocked') {
          analyticsService.track(ANALYTICS_EVENTS.SELF_ENROLL_BLOCKED, {
            ct_id: occurrence.trainingCenterId,
            occurrence_id: occurrence.id,
            source: 'makeup_agenda',
            block_reason: options.blockReason ?? 'unknown'
          })
          setState({ kind: 'blocked', reason: options.blockReason ?? null, options })
        } else {
          setState({ kind: 'ready', options })
        }
      } catch (err) {
        console.error('Error loading self-enroll checkout options:', err)
        if (!cancelled) setState({ kind: 'error' })
      }
    }

    loadOptions()
    return () => {
      cancelled = true
    }
  }, [open, occurrence, reloadKey])

  if (!occurrence) return null

  const handleConfirm = async () => {
    if (isEnrolling) return
    try {
      setIsEnrolling(true)
      await makeupService.selfEnroll(occurrence.trainingCenterId, occurrence.id)
      analyticsService.track(ANALYTICS_EVENTS.SELF_ENROLL_COMPLETED, {
        ct_id: occurrence.trainingCenterId,
        occurrence_id: occurrence.id,
        source: 'makeup_agenda'
      })
      toastManager.success('Inscrição confirmada! A aula já aparece em Meus Treinos.')
      onEnrolled()
    } catch (err) {
      console.error('Error self-enrolling with makeup credit:', err)
      const code = (err as { code?: string })?.code
      analyticsService.track(ANALYTICS_EVENTS.SELF_ENROLL_ERROR, {
        ct_id: occurrence.trainingCenterId,
        occurrence_id: occurrence.id,
        source: 'makeup_agenda',
        error_code: code ?? 'unknown'
      })
      toastManager.error(
        (code && ENROLL_ERROR_MESSAGES[code]) ??
          'Não foi possível confirmar sua inscrição. Tente novamente.'
      )
    } finally {
      setIsEnrolling(false)
    }
  }

  const occurrenceSummary = (
    <div className="rounded-lg bg-background-secondary p-4 space-y-2">
      <Body className="font-semibold text-text-primary">{occurrence.trainingCenter.name}</Body>
      <Caption className="text-text-secondary flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
        {formatDateForUI(parseAPIDate(occurrence.date))}
      </Caption>
      <Caption className="text-text-secondary flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        {formatTimeRange(occurrence.startTimeMin, occurrence.endTimeMin)}
      </Caption>
      <Caption className="text-text-secondary flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
        {occurrence.location.name}
      </Caption>
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        state.kind === 'blocked' &&
        state.reason &&
        NO_CREDIT_REASONS.includes(state.reason) &&
        state.options?.packagesForSale?.length
          ? 'Comprar créditos'
          : 'Usar reposição?'
      }
      size="sm"
    >
      {state.kind === 'loading' ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <Body className="text-text-secondary mt-3">Verificando disponibilidade...</Body>
        </div>
      ) : state.kind === 'error' ? (
        <div className="space-y-4">
          <Body className="text-feedback-error">
            Não foi possível verificar a disponibilidade. Tente novamente.
          </Body>
          <Button variant={ButtonVariant.SECONDARY} onClick={onClose} className="w-full">
            Fechar
          </Button>
        </div>
      ) : state.kind === 'blocked' ? (
        state.reason &&
        NO_CREDIT_REASONS.includes(state.reason) &&
        state.options?.packagesForSale?.length ? (
          <div className="space-y-4">
            {occurrenceSummary}
            <Body className="text-text-primary">
              Você não tem créditos disponíveis. Compre um pacote de aulas — assim que o pagamento
              for confirmado, você volta para cá e confirma sua inscrição com o crédito novo.
            </Body>
            <ClassPackagePurchaseFlow
              trainingCenterId={occurrence.trainingCenterId}
              packages={state.options.packagesForSale}
              occurrenceId={occurrence.id}
              source="checkout"
              onCompleted={() => setReloadKey((key) => key + 1)}
              onClose={onClose}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {occurrenceSummary}
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-feedback-error flex-shrink-0 mt-0.5" />
              <Body className="text-text-primary">
                {(state.reason && BLOCK_REASON_MESSAGES[state.reason]) ??
                  'Não é possível usar uma reposição nesta aula.'}
              </Body>
            </div>
            <Button variant={ButtonVariant.SECONDARY} onClick={onClose} className="w-full">
              Entendi
            </Button>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {occurrenceSummary}
          <div>
            <Body className="text-text-primary">
              {state.options.makeup?.availableCredits === 1
                ? 'Você tem 1 reposição disponível neste centro de treinamento.'
                : `Você tem ${state.options.makeup?.availableCredits ?? 0} reposições disponíveis neste centro de treinamento.`}
            </Body>
            {state.options.makeup?.nextExpiringAt && (
              <Caption className="text-text-secondary block mt-1">
                A reposição usada vence em{' '}
                {formatDateForUI(parseAPIDate(state.options.makeup.nextExpiringAt))}.
              </Caption>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={onClose}
              disabled={isEnrolling}
              className="w-full sm:flex-1"
            >
              Voltar
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={handleConfirm}
              disabled={isEnrolling}
              className="w-full sm:flex-1"
            >
              {isEnrolling ? 'Confirmando...' : 'Confirmar inscrição'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
